"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useUser from "@/lib/useUser";
import { calculateReportPdfPageCount } from "@/lib/reportPagination";
import {
  postMatchesReportDateFilter,
  reportDateFilterHasBounds,
  type ReportDateFilterValue,
} from "@/lib/util";
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Separator,
  Switch,
  Text,
} from "@radix-ui/themes";
import { IconCalendarMonth, IconDownload } from "@tabler/icons-react";
import { toast } from "sonner";
import NewLogoPicker from "@/components/CustomUiComponents/NewLogoPicker";
import * as Dialog from "@/components/CustomUiComponents/Dialog";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import {
  createCampaignReport,
  getReportDownloadUrl,
  isTerminalReportStatus,
  useCampaignReportJob,
} from "@/services/campaignReport";

type DateRangeTuple = [Date | null, Date | null];

interface Props {
  campaign: Campaign;
  tags: string[];
  types: string[];
  sortKey: string;
  sortDir: string;
  postsForReportBase: Post[];
  defaultReportDates?: ReportDateFilterValue;
  /** When set, used as the `tags` query string (encoded); else `tags.join(",")`. */
  tagsForReportUrl?: string;
}
function cloneDefaultDates(d: ReportDateFilterValue | undefined): DateRangeTuple {
  if (!d || d === null) {
    return [null, null];
  }
  if (d instanceof Date) {
    return [null, null];
  }
  return [
    d[0] ? new Date(d[0].getTime()) : null,
    d[1] ? new Date(d[1].getTime()) : null,
  ];
}

function rangeHasAnyDate(d: DateRangeTuple): boolean {
  return d[0] !== null || d[1] !== null;
}

/** Render an elapsed-ms duration as "0:42" / "2:14" so the create button shows
 *  meaningful progress instead of a spinning "Generating..." for ~2 minutes. */
function formatRunningFor(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/** Map the server's coarse phase string to a user-facing label. Falls back
 *  to "Generating" so an unknown phase from a newer server doesn't hide the
 *  status. */
function describePhase(phase: string | null | undefined): string {
  switch (phase) {
    case "rendering":
      return "Rendering";
    case "downloading":
      return "Downloading PDF";
    case "compressing":
      return "Compressing";
    default:
      return "Generating";
  }
}

export default function NewCreateReport({
  campaign,
  tags,
  types,
  sortKey,
  sortDir,
  postsForReportBase,
  defaultReportDates,
  tagsForReportUrl,
}: Props) {
  const [img, setImg] = useState(null as any);
  const { canImpersonate } = useUser();
  const [reportDates, setReportDates] = useState<DateRangeTuple>([null, null]);
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);
  const [compress, setCompress] = useState(false);

  // Async report job state. We hold the active jobId in component state and
  // let SWR poll the status endpoint every 2s; the effect below reacts to
  // terminal statuses (download or surface error) and clears the jobId.
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const downloadedJobsRef = useRef<Set<string>>(new Set());
  const { data: jobData } = useCampaignReportJob(
    activeJobId ? campaign.id : undefined,
    activeJobId,
  );

  const downloadFileName = useMemo(() => {
    if (campaign.artist?.name && campaign.song?.name) {
      return `${campaign.artist.name}-${campaign.song.name}.pdf`;
    }
    return `${campaign.name}.pdf`;
  }, [campaign.artist?.name, campaign.song?.name, campaign.name]);

  const loading =
    activeJobId !== null &&
    (!jobData || !isTerminalReportStatus(jobData.status));

  const syncFromDashboard = useCallback(() => {
    const next = cloneDefaultDates(defaultReportDates);
    setReportDates(next);
    setDateFilterEnabled(rangeHasAnyDate(next));
  }, [defaultReportDates]);

  const rangeForFilter = useMemo((): ReportDateFilterValue => {
    if (!dateFilterEnabled) {
      return null;
    }
    if (reportDates[0] === null && reportDates[1] === null) {
      return null;
    }
    return reportDates;
  }, [dateFilterEnabled, reportDates]);

  const totalMatchingPosts = postsForReportBase.length;

  const filteredPostCount = useMemo(() => {
    return postsForReportBase.filter((p) =>
      postMatchesReportDateFilter(p.postedAt, rangeForFilter)
    ).length;
  }, [postsForReportBase, rangeForFilter]);

  const hasSelectedRange =
    dateFilterEnabled && rangeHasAnyDate(reportDates);

  // React to terminal job status: trigger the browser download exactly once,
  // surface failures via toast, and clear the activeJobId so the button
  // returns to its idle state. The downloadedJobsRef guard prevents repeat
  // downloads if SWR re-fires the same terminal status (e.g. on focus).
  useEffect(() => {
    if (!activeJobId || !jobData) return;
    if (jobData.status === "done") {
      if (!downloadedJobsRef.current.has(activeJobId)) {
        downloadedJobsRef.current.add(activeJobId);
        const url = getReportDownloadUrl(
          campaign.id,
          activeJobId,
          downloadFileName,
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = downloadFileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setActiveJobId(null);
    } else if (jobData.status === "failed") {
      toast.error(jobData.error || "Report failed to generate");
      setActiveJobId(null);
    }
  }, [activeJobId, jobData, campaign.id, downloadFileName]);

  const startReport = async () => {
    let logoData: string | null = null;
    if (img) {
      logoData = await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () =>
          resolve(typeof reader.result === "string" ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(img);
      });
    }

    const range = rangeForFilter;
    const qs = new URLSearchParams();
    qs.set("domain", window.location.host);
    qs.set("reportOrigin", window.location.origin);
    qs.set(
      "types",
      Array.isArray(types) ? types.join(",") : String(types ?? ""),
    );
    qs.set(
      "tags",
      tagsForReportUrl !== undefined
        ? tagsForReportUrl
        : Array.isArray(tags)
          ? tags.join(",")
          : String(tags ?? ""),
    );
    qs.set("initialSortDir", String(sortDir));
    qs.set("initialSortKey", String(sortKey));
    qs.set(
      "totalPages",
      String(
        calculateReportPdfPageCount(filteredPostCount, {
          compactFirstPage: reportDateFilterHasBounds(range),
        }),
      ),
    );
    if (range && !(range instanceof Date)) {
      if (range[0]) qs.set("reportDateStart", range[0].toISOString());
      if (range[1]) qs.set("reportDateEnd", range[1].toISOString());
    }
    if (compress) {
      qs.set("compress", "true");
    }

    try {
      const { jobId } = await createCampaignReport({
        campaignId: campaign.id,
        query: qs,
        logo: logoData,
      });
      setActiveJobId(jobId);
    } catch (err: any) {
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "Failed to start report generation";
      toast.error(message);
    }
  };

  return (
    <Dialog.Root
      onOpenChange={(open) => {
        if (open) {
          syncFromDashboard();
        }
      }}
    >
      <Dialog.Trigger>
        <IconButton variant="ghost">
          <IconDownload className="icon" />
        </IconButton>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Content style={{ maxWidth: "520px", overflow: "visible" }}>
          <Dialog.Title style={{ alignContent: "center" }}>
            Create Report
          </Dialog.Title>

          <Text size="2" color="gray" mb="2">
            Uses your current platform and tag filters. By default every matching
            post is included; turn on the option below only if you want the PDF
            limited to posts published in a specific window.
          </Text>

          <Box
            p="3"
            style={{
              borderRadius: "var(--radius-3)",
              border: "1px solid var(--gray-a6)",
              background: "var(--gray-a2)",
            }}
          >
            <Flex align="center" justify="between" gap="2" wrap="wrap" mb="2">
              <Text weight="bold" size="3">
                Post dates
              </Text>
              <Badge size="1" color="gray" variant="soft">
                Optional
              </Badge>
            </Flex>

            <Flex align="center" gap="3" mb="3">
              <Switch
                checked={dateFilterEnabled}
                onCheckedChange={(checked) => {
                  setDateFilterEnabled(checked);
                  if (checked) {
                    setReportDates(cloneDefaultDates(defaultReportDates));
                  } else {
                    setReportDates([null, null]);
                  }
                }}
              />
              <Flex direction="column" gap="0">
                <Text size="2" weight="medium">
                  Limit PDF to a publish-date range
                </Text>
                <Text size="1" color="gray">
                  Off = all matching posts. On = only posts whose publish date
                  falls in the range you pick.
                </Text>
              </Flex>
            </Flex>

            {dateFilterEnabled && (
              <Box
                pt="2"
                style={{
                  position: "relative",
                  zIndex: 100,
                  overflow: "visible",
                }}
              >
                {!rangeHasAnyDate(reportDates) && (
                  <Text size="1" color="gray" mb="2" as="p">
                    Choose a start and end date below. Until you do, the report
                    still includes every matching post.
                  </Text>
                )}
                <DateRangePicker
                  onChange={(v) =>
                    setReportDates((v as DateRangeTuple) ?? [null, null])
                  }
                  value={reportDates}
                  calendarIcon={<IconCalendarMonth className="icon" />}
                />
                <Button
                  type="button"
                  variant="soft"
                  color="gray"
                  size="1"
                  mt="2"
                  style={{ alignSelf: "flex-start" }}
                  onClick={() => setReportDates([null, null])}
                  disabled={!rangeHasAnyDate(reportDates)}
                >
                  Clear dates
                </Button>
              </Box>
            )}

            <Separator size="4" my="3" />

            <Flex direction="column" gap="1">
              <Text size="2" color="gray">
                Posts in this PDF
              </Text>
              {hasSelectedRange ? (
                <Text size="3">
                  <Text weight="bold" as="span">
                    {filteredPostCount}
                  </Text>{" "}
                  post{filteredPostCount === 1 ? "" : "s"} in the selected date
                  range (of {totalMatchingPosts} matching your filters)
                </Text>
              ) : (
                <Text size="3">
                  <Text weight="bold" as="span">
                    {filteredPostCount}
                  </Text>{" "}
                  post{filteredPostCount === 1 ? "" : "s"}{" "}
                  <Text color="gray" size="2" as="span">
                    — full list (no date filter)
                  </Text>
                </Text>
              )}
            </Flex>
          </Box>

          <Box
            mt="3"
            p="3"
            style={{
              borderRadius: "var(--radius-3)",
              border: "1px solid var(--gray-a6)",
              background: "var(--gray-a2)",
            }}
          >
            <Flex align="center" justify="between" gap="2" wrap="wrap" mb="2">
              <Text weight="bold" size="3">
                File size
              </Text>
              <Badge size="1" color="gray" variant="soft">
                Optional
              </Badge>
            </Flex>
            <Flex align="center" gap="3">
              <Switch
                checked={compress}
                onCheckedChange={setCompress}
                disabled={loading}
              />
              <Flex direction="column" gap="0">
                <Text size="2" weight="medium">
                  Compress for email & sharing (~25 MB target)
                </Text>
                <Text size="1" color="gray">
                  Off = full-quality images. On = downsampled images so the PDF
                  fits standard email/Slack attachment limits. Adds a few
                  seconds to generation time.
                </Text>
              </Flex>
            </Flex>
          </Box>

          <Separator size="4" my="3" />

          <Flex direction="column" gap="3" align="center" py="2">
            {canImpersonate && <NewLogoPicker setImage={setImg} />}
          </Flex>

          <Flex gap="3" mt="4" justify="center">
            <Button
              onClick={() => startReport()}
              disabled={filteredPostCount === 0 || loading}
            >
              <Flex justify="center" gap="3">
                {loading
                  ? jobData?.status === "running"
                    ? `${describePhase(jobData.phase)}${
                        typeof jobData.runningForMs === "number"
                          ? ` (${formatRunningFor(jobData.runningForMs)})`
                          : "..."
                      }`
                    : "Queued..."
                  : "Create"}
                {loading && (
                  <div className="loader ease-linear rounded-full border-gray-200 !h-6 !w-6 "></div>
                )}
              </Flex>
            </Button>
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
